/**
 * Core Firebase Configuration and Initialization
 * Centralized Firebase setup with optimized performance
 */

import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { 
  getAuth as getFirebaseAuthInstance, 
  Auth, 
  initializeAuth,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  connectAuthEmulator,
  User as FirebaseUser
} from 'firebase/auth';
import type { User, UserRoleType, Address } from '@/lib/types';

// =============================================================================
// CONFIGURATION
// =============================================================================

interface FirebaseConfig {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
  measurementId?: string;
}

interface FirebaseInitResult {
  app: FirebaseApp | null;
  auth: Auth | null;
  isInitialized: boolean;
  error?: string;
}

// =============================================================================
// INITIALIZATION STATE
// =============================================================================

let firebaseResult: FirebaseInitResult | null = null;
let hasLoggedInit = false;

/**
 * Check if we're in browser environment
 */
function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Initialize Firebase with optimized lazy loading
 */
function initializeFirebase(): FirebaseInitResult {
  // Critical: Only initialize in browser environment
  if (!isBrowser()) {
    return {
      app: null,
      auth: null,
      isInitialized: false,
      error: 'Firebase not available in server/build environment'
    };
  }
  
  try {
    // Check if Firebase is already initialized
    const existingApps = getApps();
    if (existingApps.length > 0) {
      const app = existingApps[0];
      let auth: Auth;
      
      try {
        auth = getFirebaseAuthInstance(app);
      } catch {
        auth = initializeAuth(app, {
          persistence: [indexedDBLocalPersistence, browserLocalPersistence],
        });
      }
      
      // Set tenant ID if configured
      const tenantId = process.env.NEXT_PUBLIC_FIREBASE_TENANT_ID;
      if (tenantId && !auth.tenantId) {
        auth.tenantId = tenantId;
      }
      
      return { app, auth, isInitialized: true };
    }

    // Get configuration from environment
    const firebaseConfig: FirebaseConfig = {
      apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
      authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
      storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
      measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID
    };

    // Validate required configuration
    if (!firebaseConfig.apiKey || !firebaseConfig.authDomain || !firebaseConfig.projectId) {
      const missingKeys = [];      if (!firebaseConfig.apiKey) missingKeys.push('NEXT_PUBLIC_FIREBASE_API_KEY');
      if (!firebaseConfig.authDomain) missingKeys.push('NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN');      
      if (!firebaseConfig.projectId) missingKeys.push('NEXT_PUBLIC_FIREBASE_PROJECT_ID');
      
      throw new Error(`Missing required Firebase configuration: ${missingKeys.join(', ')}`);
    }

    // Initialize Firebase App
    const app = initializeApp(firebaseConfig);
    
    // Initialize Auth with persistence
    const auth = initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence],
    });

    // Configure tenant if specified
    const tenantId = process.env.NEXT_PUBLIC_FIREBASE_TENANT_ID;
    if (tenantId) {
      auth.tenantId = tenantId;
    }
    
    // Connect to Auth emulator in development
    if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_URL) {
 connectAuthEmulator(auth, process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_URL);
    }
    
    if (!hasLoggedInit) {
      console.log('🔥 Firebase initialized successfully');
      hasLoggedInit = true;
    }

    return { app, auth, isInitialized: true };

  } catch (error) {
    console.error('🚨 Firebase initialization failed:', error);
    return {
      app: null,
      auth: null,
      isInitialized: false,
      error: error instanceof Error ? error.message : 'Unknown initialization error'
    };
  }
}

/**
 * Get Firebase result with lazy initialization
 */
function getFirebaseResult(): FirebaseInitResult {
  if (!firebaseResult) {
    firebaseResult = initializeFirebase();
  }
  return firebaseResult;
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Get Firebase App instance - safe for SSR/build
 */
export function getApp(): FirebaseApp | null {
  if (!isBrowser()) return null;
  return getFirebaseResult().app;
}

/**
 * Get Firebase Auth instance - safe for SSR/build
 */
export function getAuth(): Auth | null {
  if (!isBrowser()) return null;
  return getFirebaseResult().auth;
}



/**
 * Check if Firebase is initialized
 */
export function isInitialized(): boolean {
  if (!isBrowser()) return false;
  return getFirebaseResult().isInitialized;
}

/**
 * Get initialization error if any
 */
export function getInitError(): string | undefined {
  if (!isBrowser()) return 'Not in browser environment';
  return getFirebaseResult().error;
}

/**
 * Check Firebase health status
 */
export function getHealthStatus(): { isHealthy: boolean; error?: string } {
  if (!isBrowser()) {
    return { isHealthy: false, error: 'Not in browser environment' };
  }
  
  const result = getFirebaseResult();
  return {
    isHealthy: result.isInitialized && !!result.auth,
    error: result.error
  };
}

// =============================================================================
// USER CONVERSION UTILITIES
// =============================================================================

/**
 * Convert Firebase User to application User type
 */
export function convertFirebaseUser(firebaseUser: FirebaseUser): User {
  // Parse display name for first/last name
  const displayName = firebaseUser.displayName || '';
  const nameParts = displayName.split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName: displayName,
    firstName: firstName,
    lastName: lastName,
    photoURL: firebaseUser.photoURL || '/user.png',
    phoneNumber: firebaseUser.phoneNumber || undefined,
    emailVerified: firebaseUser.emailVerified,
    disabled: false,
    
    // Default values - these should be set via custom claims
    roles: ['Member'],
    organizationId: '',
    tenantId: '',
    creationTime: firebaseUser.metadata.creationTime || new Date().toISOString(),
    lastSignInTime: firebaseUser.metadata.lastSignInTime || undefined,
    
    // Additional fields
    status: 'Active',
    address: undefined,
    dateOfBirth: undefined,
    gender: undefined,
    tags: [],
    customClaims: {},
    
    // Booking permissions (default values)
    canBookAllDay: false
  };
}

/**
 * Convert Firebase Admin User Record to application User type
 */
export function convertAdminUser(userRecord: {
  uid: string;
  email?: string;
  displayName?: string;
  photoURL?: string;
  phoneNumber?: string;
  emailVerified?: boolean;
  disabled?: boolean;
  customClaims?: Record<string, unknown>;
  metadata?: {
    creationTime?: string;
    lastSignInTime?: string;
  };
}): User {
  // Parse display name for first/last name
  const displayName = userRecord.displayName || '';
  const nameParts = displayName.split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  // Extract custom claims
  const customClaims = userRecord.customClaims || {};
  
  return {
    uid: userRecord.uid,
    email: userRecord.email || '',
    displayName: displayName,
    firstName: (typeof customClaims.firstName === 'string' ? customClaims.firstName : firstName),
    lastName: (typeof customClaims.lastName === 'string' ? customClaims.lastName : lastName),
    photoURL: userRecord.photoURL || '/user.png',
    phoneNumber: userRecord.phoneNumber || undefined,
    emailVerified: userRecord.emailVerified || false,
    disabled: userRecord.disabled || false,
    
    // From custom claims or defaults
    roles: (Array.isArray(customClaims.roles) ? customClaims.roles as UserRoleType[] : ['Member']),
    organizationId: (typeof customClaims.organizationId === 'string' ? customClaims.organizationId : ''),
    tenantId: (typeof customClaims.tenantId === 'string' ? customClaims.tenantId : ''),
    creationTime: userRecord.metadata?.creationTime || new Date().toISOString(),
    lastSignInTime: userRecord.metadata?.lastSignInTime || undefined,
    
    // Additional fields from custom claims
    address: customClaims.address as Address | undefined,
    dateOfBirth: customClaims.dateOfBirth as string | undefined,
    gender: customClaims.gender as ('Male' | 'Female' | 'Other' | 'Prefer not to say') | undefined,
    tags: (Array.isArray(customClaims.tags) ? customClaims.tags : []),
    customClaims: customClaims,
    
    // Booking permissions from custom claims
    canBookAllDay: (typeof customClaims.canBookAllDay === 'boolean' ? customClaims.canBookAllDay : false),
    
    // Additional fields
    status: 'Active'
  };
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get current Firebase user
 */
export async function getCurrentUser(): Promise<User | null> {
  const auth = getAuth();
  if (!auth?.currentUser) return null;
  
  return convertFirebaseUser(auth.currentUser);
}

/**
 * Check if user has specific role
 */
export function hasRole(user: User, role: string): boolean {
  return user.roles?.includes(role as UserRoleType) || false;
}

/**
 * Check if user is Super Admin
 */
export function isSuperAdmin(user: User): boolean {
  return hasRole(user, 'Super Admin') || user.customClaims?.isSuperAdmin === true;
}

/**
 * Check if user is Admin
 */
export function isAdmin(user: User): boolean {
  return hasRole(user, 'Admin') || user.customClaims?.isAdmin === true;
} 
